// Shared helpers for per-unit serial-number tracking. Pure array math plus a
// small write-through to the `productSerials/{serialNumber}` lookup
// collection, used by every place that claims/releases warehouse stock
// (WarehouseInventoryService, order allocation/restoration).
import { doc, serverTimestamp, Transaction } from 'firebase/firestore';
import { db } from '../firebase/config';

const SERIAL_LOOKUP_COLLECTION = 'productSerials';

export function claimSerialsFromArray(
  availableSerials: string[] | undefined,
  count: number
): { claimed: string[]; remaining: string[] } {
  const serials = availableSerials || [];
  return {
    claimed: serials.slice(0, count),
    remaining: serials.slice(count),
  };
}

export function releaseSerialsToArray(
  availableSerials: string[] | undefined,
  toRelease: string[]
): string[] {
  const serials = availableSerials || [];
  return [...serials, ...toRelease];
}

export type SerialDocStatus = 'in_stock' | 'sold' | 'returned' | 'damaged';

export interface SerialDocContext {
  productId: string;
  productName?: string;
  sku?: string;
  variantId?: string;
  variantSku?: string;
  variantName?: string;
  warehouseId: string;
  warehouseName?: string;
  orderId?: string;
  orderNumber?: string;
}

// Writes/updates a productSerials/{serialNumber} doc inside an existing
// transaction. Doc ID is the serial itself — always a plain doc-ID write,
// never a query, consistent with every other transaction in this app.
export function writeSerialStatus(
  transaction: Transaction,
  serialNumber: string,
  status: SerialDocStatus,
  context: SerialDocContext
): void {
  const ref = doc(db, SERIAL_LOOKUP_COLLECTION, serialNumber);
  const now = serverTimestamp();

  const data: Record<string, any> = {
    serialNumber,
    productId: context.productId,
    warehouseId: context.warehouseId,
    status,
  };
  if (context.productName !== undefined) data.productName = context.productName;
  if (context.sku !== undefined) data.sku = context.sku;
  if (context.variantId !== undefined) data.variantId = context.variantId;
  if (context.variantSku !== undefined) data.variantSku = context.variantSku;
  if (context.variantName !== undefined) data.variantName = context.variantName;
  if (context.warehouseName !== undefined) data.warehouseName = context.warehouseName;
  if (context.orderId !== undefined) data.orderId = context.orderId;
  if (context.orderNumber !== undefined) data.orderNumber = context.orderNumber;

  if (status === 'sold') {
    data.dateSold = now;
  } else if (status === 'returned' || status === 'damaged') {
    data.dateReturned = now;
  } else if (status === 'in_stock') {
    data.dateAdded = now;
  }

  transaction.set(ref, data, { merge: true });
}

// Guards global serial-number uniqueness: throws if any of the given serials
// already has a productSerials record (in use by any product, anywhere).
// Must be called during a transaction's read phase — before any writes —
// since it performs transaction.get reads.
export async function assertSerialsAvailable(
  transaction: Transaction,
  serials: string[]
): Promise<void> {
  for (const serial of serials) {
    const snap = await transaction.get(doc(db, SERIAL_LOOKUP_COLLECTION, serial));
    if (snap.exists()) {
      throw new Error(`Serial number "${serial}" is already in use.`);
    }
  }
}

// Reconciles the productSerials lookup collection with an admin-entered
// availableSerials list on save: newly-added serials get an 'in_stock'
// record, serials the admin removed (that were never sold — a sold serial
// is already absent from availableSerials, so it never appears in
// `previous`) have their lookup doc deleted so nothing is left dangling.
export function syncSerialNumbers(
  transaction: Transaction,
  previous: string[] | undefined,
  next: string[] | undefined,
  context: SerialDocContext
): void {
  const prevSet = new Set(previous || []);
  const nextSet = new Set(next || []);

  for (const serial of Array.from(nextSet)) {
    if (!prevSet.has(serial)) {
      writeSerialStatus(transaction, serial, 'in_stock', context);
    }
  }

  for (const serial of Array.from(prevSet)) {
    if (!nextSet.has(serial)) {
      transaction.delete(doc(db, SERIAL_LOOKUP_COLLECTION, serial));
    }
  }
}
